import { SupportedLocale } from '@amzn/red-velvet-api';
import { toOfferedLocale } from './localeUtils';

const STORAGE_KEY = 'vc_language_preference';

type CachedPreference = {
  alias: string;
  locale: SupportedLocale;
};

/**
 * Remembers the language a user just chose, so reopening the picker shows it instantly instead of
 * waiting on the server.
 *
 * Kept for the browsing session only, and matched to the user it was saved for, so nobody is ever
 * shown someone else's language. Storage failures are ignored: a missing cache only costs a request.
 */
export function readCachedLocale(alias: string): SupportedLocale | undefined {
  if (!alias) {
    return undefined;
  }

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return undefined;
    }

    const cached = JSON.parse(raw) as Partial<CachedPreference>;
    if (cached.alias !== alias) {
      return undefined;
    }

    // Checked again on the way out, so a stale or edited entry cannot show an unsupported language.
    return toOfferedLocale(cached.locale);
  } catch {
    return undefined;
  }
}

export function writeCachedLocale(alias: string, locale: SupportedLocale): void {
  if (!alias) {
    return;
  }

  try {
    const entry: CachedPreference = { alias, locale };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    return;
  }
}
